import * as T from 'three';
import { createSceneKit } from '@london-life/game/scene/kit';
import { createProps, drawAdvert, drawRiverPlots } from '@london-life/game/city/props';
import { RIVER_PLOTS } from '@london-life/contracts';
import { createInstancer } from '@london-life/game/city/instanced';
import { buildAirports } from '@london-life/game/city/airports';
import { buildStation, type TrainPaint } from '@london-life/game/city/station';
import type { Livery } from '@london-life/game/flight/jet';
import {
  AIRPORT_SITES,
  AREA_LABELS,
  BILLBOARD_SLOTS,
  CITY_RUNWAY,
  KINGS_CROSS,
  LAND,
  PLOT_SIZE,
  RIVER_PLOTS_AT,
  WATER,
  geo,
  siteAt,
  type MapPoint,
} from '@london-life/game/city/layout';

// original London adverts for the boards and plots
const ADVERTS: [string, string, string][] = [
  ['Pie & Mash Fridays', '#e8c547', '#2b2b2b'],
  ['Wharf Fitness · Join today', '#1f2a44', '#ffffff'],
  ['Sunday Roast £12', '#b8422e', '#fff4e3'],
  ['Learn to code in Poplar', '#2f7d5b', '#ffffff'],
  ['Rooms to rent · Royal Docks', '#f3efe6', '#1d2433'],
  ['Thames Rides · Book now', '#3273b5', '#ffffff'],
  ['Greenwich Market every Saturday', '#f6b83d', '#1d2433'],
  ['Jollof Fridays at the Tavern', '#7a2d6b', '#ffe9f6'],
  ['We are hiring!', '#1d2433', '#f6b83d'],
  ['Builders tea 90p', '#d9a066', '#2b1d12'],
  ['Barbers of Poplar', '#222', '#ffffff'],
  ['Night bus home', '#163a5f', '#ffe08a'],
  ['Chip shop · open late', '#e76f51', '#fff'],
  ['Dock Cinema · £5 Tuesdays', '#111', '#f1c40f'],
  ['Flat whites · Greenwich Coffee', '#6b4f3a', '#fff'],
  ['Make London yours', '#e68728', '#fff'],
  ['Five a side · Thursdays', '#2a9d8f', '#fff'],
  ['Bike repairs · same day', '#4a4e69', '#fff'],
];

// tell land from water on the real outline
export function isWater(x: number, z: number) {
  for (const { ring } of WATER) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, zi] = ring[i],
        [xj, zj] = ring[j];
      if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
    }
    if (inside) return true;
  }
  return false;
}

// build the real Docklands and Greenwich on one floating slab
// `places`: where the places stand, so no building is put on top of one
export function buildCityMap(places: { position: [number, number] }[] = []) {
  const group = new T.Group();
  const kit = createSceneKit();
  // the library's models replace the blocks once loaded (`ready` says when)
  const models = createInstancer(group);
  const props = createProps(kit, group, models);
  const { box, tree, road, roundabout, building, house, tower, board, lamp, plane, tank, crane, bus, car, train } =
    props;
  const advert = (index: number, w = 2, h = 1) => {
    const [text, background, foreground] = ADVERTS[((index % ADVERTS.length) + ADVERTS.length) % ADVERTS.length];
    // drawn at the board's own shape, so the words stay sharp on the big ones
    return drawAdvert(text, background, foreground, 512, Math.round((512 * h) / w));
  };
  const at = (lon: number, lat: number) => geo(lon, lat);
  const heading = (a: MapPoint, b: MapPoint) => -Math.atan2(b[1] - a[1], b[0] - a[0]);
  const length = (a: MapPoint, b: MapPoint) => Math.hypot(b[0] - a[0], b[1] - a[1]);

  // float the land and cut the Thames and docks into it
  box(LAND.width + 0.6, 1.1, LAND.depth + 0.6, LAND.edge, LAND.x, -0.5, LAND.z);
  box(LAND.width, 0.12, LAND.depth, LAND.ground, LAND.x, 0.06, LAND.z);
  WATER.forEach(({ ring }, index) => {
    const geometry = kit.geo(`water${index}`, () =>
      new T.ShapeGeometry(new T.Shape(ring.map(([x, z]) => new T.Vector2(x, -z)))).rotateX(-Math.PI / 2),
    );
    const mesh = new T.Mesh(geometry, kit.mat('#4f9fc3'));
    mesh.position.y = 0.14;
    group.add(mesh);
  });
  AREA_LABELS.forEach((item) => {
    const [x, z] = at(item.lon, item.lat);
    kit.label(item.text, x, z, group, item.width, item.color ?? '#5f7580');
  });

  // keep the landmarks clear and pack the rest of the streets with small buildings
  let seed = 11;
  const random = () => (seed = (seed * 48271) % 2147483647) / 2147483647;
  const reserved: [number, number, number][] = places.map((item) => [item.position[0], item.position[1], 4.6]);
  const keep = (lon: number, lat: number, r: number) => reserved.push([...at(lon, lat), r]);
  // London City's apron stays clear (the airport's boards stood there once; the streets keep their layout)
  for (const [lon, lat, r] of [
    [0.0545, 51.5031, 6.5],
    [0.0606, 51.5022, 6.5],
    [0.0482, 51.5016, 6.5],
    [0.0655, 51.5031, 6.5],
  ] as const)
    keep(lon, lat, r);
  // and nothing is built on the roads in from Heathrow, Gatwick and King's Cross
  for (const site of [...AIRPORT_SITES, KINGS_CROSS]) {
    const path = [siteAt(site, site.road[site.road.length - 1]!), ...site.joins];
    for (let i = 1; i < path.length; i += 1) {
      const [a, b] = [path[i - 1]!, path[i]!];
      const steps = Math.ceil(length(a, b) / 2.5);
      for (let k = 0; k <= steps; k += 1)
        reserved.push([a[0] + ((b[0] - a[0]) * k) / steps, a[1] + ((b[1] - a[1]) * k) / steps, 3.6]);
    }
  }
  const q = (value: number) => Math.round(value * 2) / 2;
  const walls = ['#b5553f', '#c8795b', '#f1e6d2', '#d9dde2', '#d8c49a', '#efe3cb', '#e4cdb0', '#c9b797'];
  const roofs = ['#6d5947', '#8b8580', '#4c6b85', '#9c4f3a', '#7a9a6a', '#5b6670'];
  const fillArea = (lon0: number, lat0: number, lon1: number, lat1: number, density = 0.62, tall = 0) => {
    const [x0, z1] = at(lon0, lat0),
      [x1, z0] = at(lon1, lat1);
    for (let z = z0 + 3; z < z1 - 2.4; z += 6.2)
      for (let x = x0 + 3; x < x1 - 2.4; x += 6.2) {
        const px = x + (random() - 0.5) * 2,
          pz = z + (random() - 0.5) * 2;
        if (
          isWater(px, pz) ||
          isWater(px + 2.5, pz) ||
          isWater(px - 2.5, pz) ||
          isWater(px, pz + 2.5) ||
          isWater(px, pz - 2.5)
        )
          continue;
        if (reserved.some(([rx, rz, r]) => Math.hypot(rx - px, rz - pz) < r)) continue;
        const roll = random();
        if (roll < density) {
          if (tall && random() < tall)
            tower(
              px,
              pz,
              q(4 + random() * 2),
              q(4 + random() * 2),
              q(8 + random() * 10),
              ['#7fa7c4', '#8fb4cc', '#6f97b5'][Math.floor(random() * 3)],
            );
          else
            building(
              px,
              pz,
              q(3.8 + random() * 2),
              q(3.4 + random() * 1.6),
              q(2.4 + random() * 2.6),
              walls[Math.floor(random() * walls.length)],
              roofs[Math.floor(random() * roofs.length)],
            );
        } else if (roll < density + 0.2)
          tree(px, pz, q((0.7 + random() * 0.5) * 4) / 4, random() < 0.5 ? '#5f9b52' : '#4f8f48');
        // (once a made-up person stood here: the map's people are the players now; the draw keeps the streets'
        // layout as it was)
        else if (roll < density + 0.28) random();
      }
  };
  // draw a straight road between two real points (and remember it: journeys follow the streets)
  const streets: [MapPoint, MapPoint][] = [];
  const street = (lon0: number, lat0: number, lon1: number, lat1: number, width = 4.2) => {
    const a = at(lon0, lat0),
      b = at(lon1, lat1);
    road((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, length(a, b), heading(a, b), width);
    streets.push([a, b]);
    return [a, b] as const;
  };

  // Canary Wharf, Poplar and the Isle of Dogs
  keep(-0.0195, 51.505, 8);
  keep(-0.0229, 51.5076, 5);
  keep(-0.0145, 51.5064, 5);
  keep(-0.0135, 51.4915, 7);
  keep(-0.0081, 51.487, 5);
  street(-0.034, 51.5094, -0.003, 51.5088);
  street(-0.0255, 51.504, -0.0215, 51.4895);
  street(-0.0215, 51.4895, -0.0125, 51.4868);
  street(-0.0075, 51.5055, -0.0085, 51.4895);
  street(-0.0125, 51.4868, -0.0085, 51.4895);
  street(-0.0215, 51.4895, -0.0085, 51.4895, 3);
  street(-0.0255, 51.504, -0.0075, 51.5055, 3);
  roundabout(...at(-0.0215, 51.4895), 3.4);
  roundabout(...at(-0.0075, 51.5055), 3.4);
  {
    const [x, z] = at(-0.0195, 51.505);
    tower(x, z, 7, 7, 20, '#7fa7c4', true);
    tower(x - 7, z - 1, 5, 5, 13, '#8fb4cc');
    tower(x + 7, z - 1, 5, 5, 15, '#6f97b5');
    tower(x + 1, z + 7, 5, 4.5, 11, '#9bbdd1');
    tower(x - 6, z + 7, 4.5, 4.5, 9, '#8fb4cc');
  }
  {
    const [x, z] = at(-0.0229, 51.5076);
    building(x, z, 11, 5, 4.2, '#c9a97e', '#6d5947');
  }
  {
    const [x, z] = at(-0.0145, 51.5064);
    building(x, z, 10, 6, 3.2, '#dfe6ea', '#4c6b85');
  }
  {
    const [x, z] = at(-0.0308, 51.5114);
    building(x, z, 4, 4, 5, '#efe8da', '#7a7368');
    kit.cone(2.2, 5, '#d8d2c5', x, 7.9, z, group, 4);
  }
  {
    const [x, z] = at(-0.0165, 51.5102);
    building(x, z, 8, 5, 3.6, '#f4f4f0', '#5fa8d3');
  }
  {
    const [x, z] = at(-0.0135, 51.4915);
    box(16, 0.2, 12, '#8fb86e', x, 0.14, z);
    for (const [dx, dz] of [
      [-5, -3],
      [4, 2],
      [-3, 4],
      [6, -3],
    ])
      house(x + dx, z + dz, 1.8, 1.3, '#f2e9d4', '#8d6a4e');
    for (const [dx, dz] of [
      [-6, 3],
      [2, -4],
      [6, 4],
    ])
      tree(x + dx, z + dz, 0.8, '#4f8f48');
  }
  {
    const [x, z] = at(-0.0081, 51.487);
    box(8, 0.12, 6, '#79ab63', x, 0.13, z);
    for (const [dx, dz] of [
      [-2.5, -1.5],
      [2.5, 1.5],
      [0, -2],
    ])
      tree(x + dx, z + dz, 0.8, '#4f8f48');
  }
  {
    const a = at(-0.031, 51.5085),
      b = at(-0.004, 51.5079);
    const rail = length(a, b);
    box(rail, 0.5, 1.4, '#9aa3ad', (a[0] + b[0]) / 2, 3.2, (a[1] + b[1]) / 2, heading(a, b));
    for (let t = 0.05; t < 1; t += 0.09)
      box(0.6, 3, 0.6, '#8a929b', a[0] + (b[0] - a[0]) * t, 1.6, a[1] + (b[1] - a[1]) * t);
    const dlr = train(a[0] + (b[0] - a[0]) * 0.45, a[1] + (b[1] - a[1]) * 0.45, heading(a, b));
    dlr.position.y = 3.45;
  }
  fillArea(-0.026, 51.502, -0.011, 51.5085, 0.7, 0.5);
  fillArea(-0.03, 51.5085, -0.003, 51.515, 0.55);
  fillArea(-0.029, 51.487, -0.006, 51.502, 0.5);
  fillArea(-0.045, 51.5035, -0.03, 51.515, 0.45);

  // Greenwich and the park
  keep(-0.0095, 51.4825, 5);
  keep(-0.0065, 51.4826, 7);
  keep(0.0011, 51.4772, 16);
  keep(-0.0015, 51.4769, 5);
  keep(-0.0094, 51.484, 4);
  street(-0.017, 51.4795, 0.0085, 51.4845);
  street(-0.0095, 51.478, -0.0095, 51.4835, 3);
  street(-0.003, 51.4815, 0.013, 51.479, 3);
  {
    const [x, z] = at(-0.00904, 51.48158);
    box(10, 0.1, 8, '#cbbfa5', x, 0.13, z);
    for (const [dx, dz, color] of [
      [-3, -1.5, '#e76f51'],
      [0, -1.5, '#2a9d8f'],
      [3, -1.5, '#f6b83d'],
      [-3, 1.5, '#8e5fa8'],
      [0, 1.5, '#e9c46a'],
      [3, 1.5, '#3a86ff'],
    ] as const) {
      box(2.2, 0.9, 2, '#f3e9d6', x + dx, 0.7, z + dz);
      box(2.5, 0.18, 2.3, color, x + dx, 1.25, z + dz);
    }
  }
  {
    const [x, z] = at(-0.00957, 51.48286);
    box(9, 1.2, 2.4, '#2d3440', x, 1.1, z);
    box(7, 0.5, 1.8, '#e2b56b', x, 1.9, z);
    for (const dx of [-2.5, 0, 2.5]) box(0.16, 5, 0.16, '#6b5a4a', x + dx, 4.2, z);
    box(10, 0.4, 4, '#9ad0e6', x, 0.3, z);
  }
  {
    const [x, z] = at(-0.0065, 51.4826);
    building(x - 4, z, 7, 5, 3, '#efe3cb', '#8a8176');
    building(x + 4, z, 7, 5, 3, '#efe3cb', '#8a8176');
    kit.dome(1.8, '#d9d3c5', x - 4, 3.5, z, group);
    kit.dome(1.8, '#d9d3c5', x + 4, 3.5, z, group);
  }
  {
    const [x, z] = at(-0.0032, 51.4836);
    building(x, z, 7, 4.5, 3.4, '#f2d6a7', '#4b3b31');
    box(7.2, 0.3, 1.2, '#2f5d4b', x, 2.3, z + 2.4);
  }
  {
    const [x, z] = at(-0.0115, 51.4808);
    building(x, z, 5, 4, 2.4, '#e9d8c1', '#9f5f3b');
    box(5.2, 0.25, 1, '#c2503a', x, 1.8, z + 2.1);
  }
  {
    const [x, z] = at(-0.0094, 51.4842);
    box(2.4, 0.4, 7, '#bdb092', x, 0.2, z);
    box(2, 1.4, 4, '#e8edf0', x, 1.1, z - 1);
    box(5, 1, 2, '#f6f1e5', x + 1, 0.6, z - 5);
  }
  {
    const [x, z] = at(0.0011, 51.4772);
    box(30, 0.1, 22, '#79ab63', x, 0.13, z);
    for (let i = 0; i < 26; i++)
      tree(
        x - 14 + random() * 28,
        z - 10 + random() * 20,
        q((0.7 + random() * 0.6) * 4) / 4,
        random() < 0.5 ? '#4f8f48' : '#5f9b52',
      );
  }
  {
    const [x, z] = at(-0.0015, 51.4769);
    kit.cylinder(5, 1.6, '#86b36a', x, 0.8, z, group, 3.6, 12);
    building(x, z, 4, 3.4, 2.2, '#b7322a', '#5a2a24');
    kit.dome(1.5, '#e8e4dc', x, 3, z, group);
    kit.sphere(0.4, '#d8402f', x, 5.1, z, group);
    box(0.1, 1.6, 0.1, '#444', x, 4.3, z);
  }
  fillArea(-0.018, 51.476, 0.0, 51.4865, 0.55);
  fillArea(-0.045, 51.4745, -0.018, 51.49, 0.4);

  // Greenwich Peninsula: the O2, the cable car and the riverside screens
  keep(0.0032, 51.50294, 9);
  keep(0.0043, 51.5006, 4);
  keep(0.0084, 51.5001, 3);
  street(0.004, 51.4945, 0.0045, 51.5015);
  street(0.0045, 51.5015, -0.0045, 51.5105);
  street(0.0105, 51.4975, 0.0075, 51.5035, 3);
  {
    const [x, z] = at(0.0032, 51.50294);
    kit.dome(7, '#f4f4f0', x, 0.2, z, group);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const mast = box(0.22, 10, 0.22, '#f2c744', x + Math.cos(a) * 6.8, 4.5, z + Math.sin(a) * 6.8);
      mast.rotation.z = Math.cos(a) * 0.35;
      mast.rotation.x = -Math.sin(a) * 0.35;
    }
  }
  {
    const [x, z] = at(0.0043, 51.5006);
    building(x, z, 6, 4, 2.4, '#dfe6ea', '#4c6b85');
  }
  {
    const a = at(0.0084, 51.5001),
      b = at(0.0183, 51.5078);
    const span = length(a, b);
    box(0.5, 9, 0.5, '#8e9aa6', a[0], 4.7, a[1]);
    box(0.5, 9, 0.5, '#8e9aa6', b[0], 4.7, b[1]);
    box(span, 0.12, 0.12, '#3c4654', (a[0] + b[0]) / 2, 9.1, (a[1] + b[1]) / 2, heading(a, b));
    for (const t of [0.25, 0.5, 0.75])
      box(1.3, 1, 0.9, '#d93b47', a[0] + (b[0] - a[0]) * t, 8.4, a[1] + (b[1] - a[1]) * t);
  }
  fillArea(-0.001, 51.4935, 0.012, 51.506, 0.5);

  // Silvertown: the refinery, the Thames Barrier and the river plots
  keep(0.038, 51.5001, 9);
  keep(0.0373, 51.4969, 6);
  street(0.016, 51.5025, 0.045, 51.5012);
  street(0.045, 51.5012, 0.07, 51.5035);
  {
    const [x, z] = at(0.038, 51.5001);
    box(26, 0.1, 12, '#e3c38f', x, 0.13, z);
    for (const [dx, dz, r] of [
      [-8, -4, 3.6],
      [-1, -4, 3.6],
      [-4.5, 3, 3.2],
    ] as const)
      tank(x + dx, z + dz, r, 2.4);
    crane(x + 7, z - 5, 0.6, 8);
    building(x + 8, z + 4, 9, 5, 3.2, '#d8d0c3', '#6b7078');
    box(0.9, 10, 0.9, '#8e9aa6', x + 12, 5.2, z - 2);
    kit.sphere(0.9, '#f4a261', x + 12, 10.6, z - 2, group);
  }
  {
    const a = at(0.0365, 51.4955),
      b = at(0.0379, 51.4985);
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      const x = a[0] + (b[0] - a[0]) * t,
        z = a[1] + (b[1] - a[1]) * t;
      box(1.2, 1.4, 2.2, '#c9d1d9', x, 0.9, z, heading(a, b));
      kit.cone(0.9, 1.4, '#e4e9ee', x, 2.3, z, group, 4);
    }
  }
  // the river plots: a plate on the water; players' pictures are drawn onto it (`plots.show`)
  const plate = (() => {
    const [x, z] = at(RIVER_PLOTS_AT.lon, RIVER_PLOTS_AT.lat);
    const w = RIVER_PLOTS.columns * PLOT_SIZE,
      d = RIVER_PLOTS.rows * PLOT_SIZE;
    box(w, 0.5, d, '#7fb5cb', x, 0.05, z);
    const picture = kit.picture(drawRiverPlots(RIVER_PLOTS.columns, RIVER_PLOTS.rows, []), w, d, x, 0.32, z, group);
    return { picture, w, d };
  })();
  // and its name on the bank stays clear of buildings
  keep(RIVER_PLOTS_AT.lon, 51.4938, 13);
  fillArea(0.014, 51.4975, 0.036, 51.503, 0.5);
  fillArea(0.044, 51.497, 0.07, 51.5035, 0.4);
  fillArea(0.006, 51.484, 0.07, 51.4955, 0.35);

  // Royal Docks: the estate, ExCeL and London City Airport between the docks
  keep(0.0235, 51.5042, 8);
  keep(0.0288, 51.5082, 9);
  keep(0.0505, 51.5038, 6);
  street(0.015, 51.5095, 0.075, 51.5085);
  {
    const [x, z] = at(0.0235, 51.5042);
    road(x, z, 24, 0, 3);
    for (let column = 0; column < 5; column++)
      for (let row = 0; row < 2; row++) {
        const hx = x - 10 + column * 5,
          hz = z + (row ? 4.2 : -4.2);
        box(4, 0.06, 3.2, '#d7cdaa', hx, 0.13, hz);
        house(
          hx - 0.7,
          hz,
          2,
          1.7,
          ['#f6efe1', '#efe2cf', '#fbf3e4'][column % 3],
          ['#2a9d8f', '#3f7f6f', '#56a08a'][(column + row) % 3],
          0,
          1.4,
        );
        tree(hx + 1.4, hz + 0.5, 0.4, '#5f9b52');
      }
  }
  {
    const [x, z] = at(0.0288, 51.5082);
    building(x, z, 22, 7, 4, '#e6ebee', '#9aa6b0');
    box(21, 0.3, 6.4, '#9ed6ea', x, 4.7, z);
  }
  {
    const a = CITY_RUNWAY.from,
      b = CITY_RUNWAY.to;
    const run = length(a, b),
      angle = heading(a, b);
    const cx = (a[0] + b[0]) / 2,
      cz = (a[1] + b[1]) / 2;
    box(run, 0.14, 4.2, '#4b545e', cx, 0.2, cz, angle);
    const dashes = new T.Object3D();
    dashes.position.set(cx, 0, cz);
    dashes.rotation.y = angle;
    group.add(dashes);
    for (let d = -run / 2 + 3; d < run / 2 - 2; d += 4) kit.box(2, 0.02, 0.3, '#f1f1e6', d, 0.3, 0, dashes);
    const [tx, tz] = at(0.047, 51.5036);
    building(tx, tz, 16, 4, 3, '#e6ebee', '#9aa6b0');
    box(15, 0.3, 3.6, '#9ed6ea', tx, 3.7, tz);
    for (let i = 0; i < 3; i++) plane(tx - 6 + i * 6, tz - 4.2, Math.PI / 2, '#f4f6f8', i % 2 ? '#2f6fb3' : '#c8102e');
    building(tx + 12, tz, 3, 3, 6, '#f1f1ee', '#3c4654');
  }
  fillArea(0.013, 51.5082, 0.075, 51.5145, 0.45);

  // keep a few London vehicles still until traffic arrives
  {
    const [x, z] = at(-0.018, 51.5091);
    bus(x, z + 1.1, Math.PI);
    car(x + 14, z - 1.1, 0, '#1f242b');
  }
  {
    const [x, z] = at(-0.004, 51.4818);
    bus(x, z - 1.1, 0);
    car(x - 12, z + 1.1, Math.PI, '#c9d1d9');
    car(x + 10, z + 1.1, Math.PI, '#a63d2f');
  }
  {
    const [x, z] = at(0.03, 51.5017);
    car(x, z, 0.2, '#e9b526');
    car(x + 16, z - 1, 0.2, '#1f242b');
  }
  for (let i = 0; i < 40; i++) {
    const [x, z] = at(-0.044 + random() * 0.13, 51.472 + random() * 0.052);
    if (!isWater(x, z) && !reserved.some(([rx, rz, r]) => Math.hypot(rx - x, rz - z) < r)) lamp(x, z);
  }

  // Heathrow and Gatwick, joined to the map's edges
  const airports = buildAirports(kit, props, group);
  streets.push(...airports.streets);
  // King's Cross, joined to the north edge
  const station = buildStation(kit, props, group);
  streets.push(...station.streets);

  // the advert boards: one row along the top of the map, big and clear, of different sizes. Each is a screen:
  // London's own advert until players book it, then their pictures in turn (`show`)
  const extra = new Set<T.Texture>();
  const boards = BILLBOARD_SLOTS.map((slot) => {
    const { picture } = board(
      slot.x,
      slot.z,
      slot.w,
      slot.h,
      advert(slot.advert, slot.w, slot.h),
      slot.turn,
      slot.lift,
    );
    const material = picture.material as T.MeshBasicMaterial;
    const own = material.map;
    let shown: T.Texture | null = null;
    const corner = new T.Vector3();
    return {
      number: slot.number,
      // a picture drawn at the board's shape, or null for London's own advert again
      show(art: HTMLCanvasElement | null) {
        if (shown) {
          extra.delete(shown);
          shown.dispose();
        }
        shown = art ? new T.CanvasTexture(art) : null;
        if (shown) {
          shown.colorSpace = T.SRGBColorSpace;
          extra.add(shown);
        }
        material.map = shown ?? own;
        material.needsUpdate = true;
      },
      // the screen's four corners in the world (to find it on the screen)
      corners() {
        picture.updateWorldMatrix(true, false);
        return [
          [-1, -1],
          [1, -1],
          [1, 1],
          [-1, 1],
        ].map(([x, y]) => picture.localToWorld(corner.set((x! * slot.w) / 2, (y! * slot.h) / 2, 0)).clone());
      },
    };
  });

  // the river plots' plate: drawn again whenever what is held on it changes
  const plotMaterial = plate.picture.material as T.MeshBasicMaterial;
  let plotTexture: T.Texture | null = null;
  const plotCorner = new T.Vector3();
  const plots = {
    show(art: HTMLCanvasElement) {
      if (plotTexture) {
        extra.delete(plotTexture);
        plotTexture.dispose();
      }
      plotTexture = new T.CanvasTexture(art);
      plotTexture.colorSpace = T.SRGBColorSpace;
      extra.add(plotTexture);
      plotMaterial.map = plotTexture;
      plotMaterial.needsUpdate = true;
    },
    // the plate's four corners in the world (to find it on the screen)
    corners() {
      plate.picture.updateWorldMatrix(true, false);
      return [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ].map(([x, y]) => plate.picture.localToWorld(plotCorner.set((x! * plate.w) / 2, (y! * plate.d) / 2, 0)).clone());
    },
  };

  group.rotation.y = 0.06;
  return {
    group,
    // the streets (journeys follow them)
    streets,
    // swap the library's models in (only where WebGL draws: the still picture keeps the blocks)
    loadModels: models.build,
    boards,
    plots,
    // the jets parked at the airports' gates, in the airline's livery (none until it is known)
    setLivery: (livery: Livery | null) => airports.setLivery(livery),
    // the trains waiting at King's Cross's platforms, in the lines' paint, and the tracks the map's trains run on
    setTrains: (paints: TrainPaint[]) => station.setTrains(paints),
    tracks: station.tracks,
    dispose() {
      airports.dispose();
      station.dispose();
      for (const texture of extra) texture.dispose();
      models.dispose();
      kit.dispose();
    },
  };
}
